import { useCallback, useEffect, useMemo, useState } from 'react';
import useDebounce from '../../../../../hooks/useDebounce/useDebounce';
import useSearchParams from '../../../../../hooks/useSearchParams';
import { RadioOption } from '../../components/RadioFilter/RadioFilter';
import {
  CleanFilter,
  CommonFilterHookReturn,
  GetSearchParamKeys,
  HandleResetToDefaultValue,
} from '../commonHookReturn.types';
import { MILLISECONDS_TO_APPLY_FILTER } from '../constants';

interface UseRadioFilterParams {
  key: string;
  options: RadioOption[];
  defaultValue?: string;
  shouldIgnoreDefaultFilters?: boolean;
}

interface UseRadioFilterOutput extends CommonFilterHookReturn {
  key: string;
  options: RadioOption[];
  selectedValue: string | null;
  selectValue: (newValue: string) => void;
  hasSelectedValue: boolean;
  handleUpdateInternalState: (expandableState: boolean) => void;
}

const useRadioFilter = ({
  key,
  options,
  defaultValue,
  shouldIgnoreDefaultFilters = true,
}: UseRadioFilterParams): UseRadioFilterOutput => {
  const { searchParams, setSearchParams } = useSearchParams();

  const memorizedOptions = useMemo(() => options, []);

  const getNormalizedDefaultValue = (userDefaultValue: string) => {
    const valueExists = options.some((option) => option.value === userDefaultValue);
    if (valueExists) {
      return userDefaultValue;
    }
    return null;
  };

  const getDefaultSelectedOptions = () => {
    const userDefaultValue = shouldIgnoreDefaultFilters ? searchParams[key] : defaultValue;
    const normalizedDefaultValue = getNormalizedDefaultValue(userDefaultValue);
    return normalizedDefaultValue;
  };

  const [selectedValue, setSelectedValue] = useState(getDefaultSelectedOptions);
  const debouncedValue = useDebounce(selectedValue, MILLISECONDS_TO_APPLY_FILTER);
  useEffect(() => {
    setSearchParams({
      [key]: debouncedValue || undefined,
    });
  }, [debouncedValue]);

  const [hasSelectedValue, setHasSelectedValue] = useState(() => !!getDefaultSelectedOptions());
  const handleUpdateInternalState = useCallback(
    (isExpandable: boolean) => {
      if (isExpandable) {
        return;
      }
      setHasSelectedValue(!!selectedValue);
    },
    [selectedValue],
  );

  const selectValue = (value: string) => setSelectedValue(value);

  const getSearchParamKeys: GetSearchParamKeys = () => key;
  const cleanFilter: CleanFilter = () => setSelectedValue(null);
  const handleResetToDefaultValue: HandleResetToDefaultValue = () => {
    if (!defaultValue) {
      cleanFilter();
      return;
    }
    const formattedValue = getNormalizedDefaultValue(defaultValue);
    setSelectedValue(formattedValue);
  };

  return {
    key,
    selectedValue,
    options: memorizedOptions,
    selectValue,
    cleanFilter,
    getSearchParamKeys,
    hasSelectedValue,
    handleUpdateInternalState,
    handleResetToDefaultValue,
  };
};

export default useRadioFilter;
